<script setup lang="ts">
import {
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClockIcon,
  CommandLineIcon,
  CurrencyEuroIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline'
import { MARKETING_ROUTES, MARKETING_SCREENSHOTS } from '~~/shared/config/marketing'
import type { MarketingFaqItem, MarketingJourneyStep } from '~/types/marketing'

definePageMeta({
  layout: 'marketing',
})

useMarketingSeo({
  title: 'AppTrident | Sélectionnez, analysez et suivez vos actions',
  description: 'Passez de plus de 4 000 actions à une décision d’investissement claire avec le screener, la valorisation et le suivi de portefeuille AppTrident.',
  path: MARKETING_ROUTES.home,
})

const painPoints = [
  {
    icon: MagnifyingGlassIcon,
    title: 'Trop d’entreprises à comparer',
    description: 'Entre les indices, les secteurs et les milliers d’actions cotées, vous ne savez pas lesquelles méritent réellement votre temps.',
  },
  {
    icon: CircleStackIcon,
    title: 'Des données dispersées',
    description: 'Croissance, rentabilité, dette, valorisation : les chiffres sont partout, mais rarement organisés pour faciliter une décision.',
  },
  {
    icon: CurrencyEuroIcon,
    title: 'Le doute au moment d’acheter',
    description: 'Même après avoir trouvé une belle entreprise, il reste deux questions : est-ce le bon prix et quelle position renforcer maintenant ?',
  },
]

const proofStats = [
  { value: '4 000+', label: 'des plus grandes entreprises du monde' },
  { value: '30', label: 'critères clés pour investir dans la qualité' },
  { value: '20 ans', label: 'd’historique disponible' },
  { value: '1 200+', label: 'investisseurs engagés dans la communauté', href: `${MARKETING_ROUTES.home}#avis` },
]

const journey: MarketingJourneyStep[] = [
  {
    number: '01',
    eyebrow: 'Découvrir',
    title: 'Réduisez le monde à quelques entreprises solides',
    description: 'Filtrez plus de 4 000 entreprises, utilisez le Score Trident et repérez les meilleures opportunités dans le Podium.',
    tools: ['Screener', 'Score Trident', 'Podium', 'Indices et portefeuilles de gourous'],
    href: MARKETING_ROUTES.discover,
    linkLabel: 'Explorer les outils de sélection',
  },
  {
    number: '02',
    eyebrow: 'Analyser',
    title: 'Comprenez ce que vous achetez',
    description: 'Reliez le cours de Bourse aux bénéfices, à la croissance et à la valorisation grâce à des graphiques lisibles et des hypothèses transparentes.',
    tools: ['Fiche entreprise', 'T-Graph', 'Comparaisons', 'Scénarios de valorisation'],
    href: MARKETING_ROUTES.analyze,
    linkLabel: 'Explorer les outils d’analyse',
  },
  {
    number: '03',
    eyebrow: 'Décider & Suivre',
    title: 'Transformez l’analyse en une action concrète',
    description: 'Identifiez la position à renforcer, adaptez le montant à votre plan puis mesurez votre progression au fil du temps.',
    tools: ['Page Achat', 'Mode Avancé', 'Suivi de performance', 'Tableau de progression'],
    href: MARKETING_ROUTES.decide,
    linkLabel: 'Explorer les outils de pilotage',
  },
]

const included = [
  { icon: CommandLineIcon, title: 'Toute l’AppTrident', description: 'Screener, Podium, analyse, valorisation, page Achat, performance et progression.' },
  { icon: AcademicCapIcon, title: 'La Méthode Trident', description: 'Nos formations vidéos pour mettre en place votre routine et apprendre à analyser une entreprise.' },
  { icon: UserGroupIcon, title: 'La communauté Skool', description: 'Plus de 1 200 investisseurs avec qui poser vos questions, partager vos analyses et progresser.' },
  { icon: ArrowTrendingUpIcon, title: 'Les analyses régulières', description: 'Le suivi du portefeuille Trident, les décisions d’investissement et les mises à jour importantes.' },
  { icon: ChartBarSquareIcon, title: 'Les mises à jour produit', description: 'Une application enrichie en continu, sans avoir à racheter une nouvelle version.' },
  { icon: ShieldCheckIcon, title: 'FlashFiscal', description: 'Un partenariat inclus dans notre offre pour déclarer vos revenus boursiers en quelques clics.' },
]

const faqItems: MarketingFaqItem[] = [
  {
    question: 'L\'AppTrident est-elle une application ou une formation ?',
    answer: 'L\'AppTrident est le cœur de l’offre : une application web qui vous aide à découvrir, analyser, valoriser et suivre vos investissements. La formation Méthode Trident, les analyses régulières et la communauté Skool sont incluses pour vous aider à utiliser l’outil et comprendre la démarche.',
  },
  {
    question: 'Est-ce adapté si je débute en Bourse ?',
    answer: 'Oui. Le mode Simple reprend les sélections et hypothèses Trident, tandis que le parcours Start vous guide pour ouvrir un compte, effectuer un premier achat et mettre en place une routine. Les investisseurs plus expérimentés peuvent utiliser le mode Avancé et personnaliser leurs hypothèses.',
  },
  {
    question: 'Combien de temps faut-il y consacrer ?',
    answer: 'Vous pouvez utiliser la recommandation et les hypothèses Trident pour prendre votre décision hebdomadaire en quelques minutes. Si vous souhaitez aller plus loin, le screener, les analyses et le mode Avancé permettent d’étudier chaque entreprise en détail.',
  },
  {
    question: 'L\'AppTrident indique-t-elle automatiquement quelle action renforcer ?',
    answer: 'La page Achat classe les entreprises qualifiées à partir de leur rendement estimé et de leur poids dans votre portefeuille. Elle met en avant la position éligible sur laquelle vous êtes le moins exposé, puis suggère un montant cohérent avec votre plan d’investissement.',
  },
  {
    question: 'Comment les estimations de rendement sont-elles calculées ?',
    answer: 'L\'AppTrident projette un indicateur fondamental, comme le bénéfice, le flux de trésorerie ou le chiffre d’affaires, avec une hypothèse de croissance et un multiple de valorisation futur. Ces paramètres sont visibles et modifiables. Le résultat est un scénario de travail, pas une garantie de performance.',
  },
  {
    question: 'Que comprend l’abonnement annuel ?',
    answer: 'L’abonnement comprend l’accès complet à L\'AppTrident, ses mises à jour, la formation Méthode Trident, les analyses régulières, la communauté Skool et la licence FlashFiscal (pour IBKR, Degiro et TradeRepublic).',
  },
  {
    question: 'Comment fonctionnent le renouvellement et la résiliation ?',
    answer: 'L’abonnement coûte 397 € et est facturé pour une année. Il est reconduit à chaque date anniversaire jusqu’à résiliation. Vous recevez un rappel avant le renouvellement et pouvez gérer la résiliation depuis votre espace personnel, selon les modalités des CGV.',
  },
  {
    question: 'Comment fonctionne la garantie de 60 jours ?',
    answer: 'Vous disposez de 60 jours à compter de votre commande pour tester le service. S’il ne vous convient pas, vous pouvez demander un remboursement intégral par e-mail. Les modalités à jour figurent dans les Conditions Générales de Vente.',
  },
  {
    question: 'L\'AppTrident remplace-t-elle un conseil financier personnalisé ?',
    answer: 'Non. L\'AppTrident organise des données, fournit des scénarios et vous aide à appliquer une méthode d’analyse. Vous restez responsable de vos décisions et devez tenir compte de votre situation, de vos objectifs et de votre tolérance au risque.',
  },
]

const runtimeConfig = useRuntimeConfig()
const siteUrl = String(runtimeConfig.public.SITE_URL).replace(/\/$/, '')
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
        url: `${siteUrl}${MARKETING_ROUTES.home}`,
      }),
    },
  ],
})

const heroSection = ref<HTMLElement | null>(null)
const journeySection = ref<HTMLElement | null>(null)
const testimonialsSection = ref<HTMLElement | null>(null)
const pricingSection = ref<HTMLElement | null>(null)
const finalCtaSection = ref<HTMLElement | null>(null)

useMarketingSectionView({ page: 'landing', section: 'hero', target: heroSection })
useMarketingSectionView({ page: 'landing', section: 'journey', target: journeySection })
useMarketingSectionView({ page: 'landing', section: 'testimonials', target: testimonialsSection })
useMarketingSectionView({ page: 'landing', section: 'pricing', target: pricingSection })
useMarketingSectionView({ page: 'landing', section: 'final_cta', target: finalCtaSection })

const route = useRoute()
const { $posthog } = useNuxtApp()

function trackFeaturePageClick(step: MarketingJourneyStep): void {
  $posthog?.capture('marketing_feature_page_clicked', {
    page: 'landing',
    path: route.path,
    section: 'journey',
    label: step.linkLabel,
    destination: step.href,
  })
}
</script>

<template>
  <div>
    <section ref="heroSection" class="relative isolate overflow-hidden bg-slate-950">
      <div class="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(49,141,231,0.28),transparent_35%),radial-gradient(circle_at_80%_70%,rgba(106,168,79,0.16),transparent_30%)]" />
      <div class="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-28">
        <div>
          <div class="inline-flex items-center gap-2 rounded-full border border-sky-blue-400/25 bg-sky-blue-400/10 px-3 py-1.5 text-sm font-semibold text-sky-blue-100">
            <span class="h-1.5 w-1.5 rounded-full bg-green-cucumber-400" />
            La plateforme française pour investir à long terme
          </div>
          <h1 class="mt-6 text-balance text-4xl font-bold tracking-[-0.025em] text-white sm:text-5xl lg:leading-[1.05]">
            Passez de plus de 4 000 actions à une décision d’investissement claire.
          </h1>
          <p class="mt-6 max-w-2xl text-pretty text-lg leading-8 text-slate-300">
            En 5 minutes par semaine, identifiez l’action à renforcer et suivez votre portefeuille. Tout ça grâce aux 30 critères fondamentaux qui comptent pour investir dans des entreprises de qualité.
          </p>
          <div class="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <MarketingCta :href="`${MARKETING_ROUTES.home}#tarif`" label="Accéder à l’AppTrident" page="landing" section="hero" destination="anchor" variant="light" class="w-full sm:w-auto" />
            <MarketingCta :href="`${MARKETING_ROUTES.home}#fonctionnement`" label="Découvrir le fonctionnement" page="landing" section="hero" destination="anchor" variant="secondary" class="w-full sm:w-auto" />
          </div>
          <div class="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-slate-300">
            <span class="flex items-center gap-1.5"><CheckCircleIcon class="h-4 w-4 text-green-cucumber-400" /> Des décisions fondées sur des critères visibles</span>
            <span class="flex items-center gap-1.5"><ShieldCheckIcon class="h-4 w-4 text-green-cucumber-400" /> Garantie satisfait ou remboursé 60 jours</span>
          </div>
        </div>
        <div class="relative lg:pl-4">
          <div class="absolute -inset-8 -z-10 rounded-full bg-sky-blue-500/10 blur-3xl" />
          <MarketingProductPreview type="decision" :src="MARKETING_SCREENSHOTS.purchase" alt="Page Achat AppTrident présentant une action à renforcer, sa valorisation et l’allocation du portefeuille" caption="La page Achat transforme vos hypothèses de valorisation et votre allocation en une action d’achat concrète." priority />
        </div>
      </div>
    </section>

    <section class="border-b border-slate-200 bg-white" aria-label="L'AppTrident en chiffres">
      <div class="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-slate-200 sm:grid-cols-4">
        <div v-for="stat in proofStats" :key="stat.label" class="flex flex-col items-center justify-center bg-white px-4 py-7 text-center">
          <p class="text-2xl font-bold text-slate-950 sm:text-3xl">{{ stat.value }}</p>
          <p class="mt-1 text-sm font-medium text-slate-600">{{ stat.label }}</p>
          <NuxtLink v-if="stat.href" :to="stat.href" class="mt-2 inline-flex rounded text-sm font-bold text-sky-blue-700 underline decoration-sky-blue-200 underline-offset-4 hover:text-sky-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-2">Voir tous les avis</NuxtLink>
        </div>
      </div>
    </section>

    <section class="bg-slate-50 py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Le problème" title="Les données ne manquent pas. Ce qui manque, c’est un chemin clair jusqu’à la décision." description="L'AppTrident a été conçu pour relier les trois questions qui restent souvent séparées : quelle entreprise étudier, à quel prix l’acheter et comment l’intégrer à votre portefeuille." />
        <div class="mt-12 grid divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white md:grid-cols-3 md:divide-x md:divide-y-0">
          <article v-for="item in painPoints" :key="item.title" class="p-6 sm:p-7">
            <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-blue-50 text-sky-blue-700"><component :is="item.icon" class="h-6 w-6" aria-hidden="true" /></div>
            <h3 class="mt-5 text-lg font-bold text-slate-950">{{ item.title }}</h3>
            <p class="mt-3 text-sm leading-6 text-slate-600">{{ item.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section id="fonctionnement" ref="journeySection" class="scroll-mt-24 bg-white py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="3 étapes dédiées" title="De la découverte au suivi, sans perdre le fil" description="Explorez les outils AppTrident en détail et découvrez comment ils s’intègrent dans votre routine d’investissement." />
        <div class="mt-14 grid gap-6 lg:grid-cols-3">
          <article v-for="step in journey" :key="step.number" class="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-[transform,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:border-sky-blue-200 hover:shadow-lg">
            <span class="absolute right-5 top-3 text-6xl font-black text-slate-100">{{ step.number }}</span>
            <p class="relative text-sm font-semibold text-sky-blue-700">{{ step.eyebrow }}</p>
            <h3 class="relative mt-8 text-xl font-bold leading-7 text-slate-950">{{ step.title }}</h3>
            <p class="relative mt-4 text-sm leading-6 text-slate-600">{{ step.description }}</p>
            <div class="relative mt-6 flex-1 border-t border-slate-100 pt-5">
              <p class="text-sm font-semibold text-slate-600">Outils concernés sur l’AppTrident</p>
              <ul class="mt-3 grid gap-2">
                <li v-for="tool in step.tools" :key="tool" class="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <CheckCircleIcon class="h-4 w-4 shrink-0 text-green-cucumber-500" aria-hidden="true" />
                  {{ tool }}
                </li>
              </ul>
            </div>
            <NuxtLink :to="step.href" class="relative mt-7 inline-flex w-full items-center justify-between rounded-xl bg-sky-blue-600 px-5 py-3.5 text-sm font-bold text-white shadow-md shadow-sky-blue-900/10 transition hover:bg-sky-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue-500 focus-visible:ring-offset-2" @click="trackFeaturePageClick(step)">
              <span>{{ step.linkLabel }}</span>
              <span aria-hidden="true">→</span>
            </NuxtLink>
          </article>
        </div>
      </div>
    </section>

    <section class="overflow-hidden bg-sky-blue-900 py-20 sm:py-28">
      <div class="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <MarketingSectionHeading align="left" inverse eyebrow="Une méthode visible" title="Comprenez pourquoi une entreprise ressort et pourquoi son prix compte." description="Le Score et le Rang Trident synthétisent la qualité. Les graphiques d’analyse et de valorisation vous montrent ensuite les données et hypothèses derrière chaque scénario." />
          <ul class="mt-8 space-y-4 text-sm leading-6 text-sky-blue-100">
            <li v-for="point in ['Les critères de croissance, rentabilité, solidité financière et valorisation restent accessibles.', 'Chaque projection relie le prix futur à un indicateur fondamental et à des hypothèses explicites.', 'Le mode Avancé vous permet de modifier les entreprises et les hypothèses suivies.']" :key="point" class="flex gap-3"><CheckCircleIcon class="mt-0.5 h-5 w-5 shrink-0 text-green-cucumber-300" />{{ point }}</li>
          </ul>
          <MarketingCta :href="MARKETING_ROUTES.analyze" label="Découvrir l’analyse AppTrident" page="landing" section="product-proof" destination="feature" variant="light" class="mt-8" />
        </div>
        <MarketingProductPreview type="analyze" :src="MARKETING_SCREENSHOTS.valuation" alt="Graphique AppTrident comparant le cours d’une action, son fondamental et sa valorisation" caption="Les scénarios sont construits à partir d’hypothèses visibles et modifiables, jamais d’une promesse de rendement." />
      </div>
    </section>

    <section class="bg-white py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Tout est compris" title="L’application d’abord. L’accompagnement autour." description="Votre abonnement donne accès au logiciel complet et aux ressources nécessaires pour l’utiliser avec méthode, quel que soit votre niveau de départ." />
        <div class="mt-12 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3">
          <article v-for="item in included" :key="item.title" class="flex gap-4 border-t border-slate-200 py-6">
            <component :is="item.icon" class="mt-0.5 h-7 w-7 shrink-0 text-sky-blue-700" aria-hidden="true" />
            <div><h3 class="font-bold text-slate-950">{{ item.title }}</h3><p class="mt-2 text-sm leading-6 text-slate-600">{{ item.description }}</p></div>
          </article>
        </div>
      </div>
    </section>

    <section id="avis" ref="testimonialsSection" class="scroll-mt-24 border-y border-slate-200 bg-slate-50 py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Ils utilisent Trident" title="Des investisseurs qui voulaient surtout plus de clarté" description="Découvrez leurs retours écrits et vidéo sur l’application, la méthode et l’accompagnement." />
        <div class="mt-12"><MarketingTestimonials /></div>
      </div>
    </section>

    <section id="tarif" ref="pricingSection" class="scroll-mt-24 bg-white py-20 sm:py-28">
      <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Une offre unique" title="Accédez à toute l’AppTrident" description="Pas de niveaux à comparer ni de fonctionnalité essentielle réservée à un plan supérieur. Vous accédez au produit complet et à toutes les ressources incluses." />
        <div class="mx-auto mt-12 max-w-3xl overflow-hidden rounded-2xl bg-white shadow-lg shadow-sky-blue-900/10 ring-1 ring-sky-blue-200">
          <div class="grid lg:grid-cols-[1fr_0.9fr]">
            <div class="p-7 sm:p-10">
              <p class="text-sm font-bold uppercase tracking-[0.16em] text-sky-blue-700">Abonnement annuel</p>
              <div class="mt-4 flex items-end gap-2"><span class="text-5xl font-bold tracking-tight text-slate-950">397 €</span><span class="pb-1 text-sm font-medium text-slate-500">par an</span></div>
              <p class="mt-4 text-sm leading-6 text-slate-600">Facturé en une fois pour douze mois d’accès. Renouvellement annuel jusqu’à résiliation.</p>
              <MarketingCta label="Rejoindre l'AppTrident dès maintenant" page="landing" section="pricing" :block="true" class="mt-7" />
              <p class="mt-3 text-center text-sm text-slate-600">Paiement sécurisé par Stripe</p>
            </div>
            <div class="border-t border-sky-blue-100 bg-sky-blue-50 p-7 sm:p-10 lg:border-l lg:border-t-0">
              <p class="font-bold text-slate-950">Votre accès comprend :</p>
              <ul class="mt-5 space-y-3 text-sm leading-5 text-slate-700">
                <li v-for="line in ['L’intégralité de l’AppTrident', 'La formation Méthode Trident', 'Les analyses et mises à jour', 'La communauté privée Skool', 'La licence FlashFiscal', 'La garantie de 60 jours']" :key="line" class="flex gap-2.5"><CheckCircleIcon class="h-5 w-5 shrink-0 text-green-cucumber-600" />{{ line }}</li>
              </ul>
            </div>
          </div>
          <div class="flex items-start gap-4 border-t border-sky-blue-100 bg-slate-950 px-7 py-6 text-white sm:px-10">
            <ShieldCheckIcon class="h-8 w-8 shrink-0 text-green-cucumber-300" aria-hidden="true" />
            <div><p class="font-bold">Vous avez 60 jours pour décider.</p><p class="mt-1 text-sm leading-6 text-slate-300">Testez l’application et les ressources incluses. Si le service ne vous convient pas, demandez le remboursement intégral selon les modalités des CGV.</p></div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-slate-50 py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Questions fréquentes" title="Ce qu’il faut savoir avant de rejoindre AppTrident" />
        <div class="mt-12"><MarketingFaq :items="faqItems" /></div>
      </div>
    </section>

    <section ref="finalCtaSection" class="relative overflow-hidden bg-sky-blue-700 py-20">
      <div class="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(255,255,255,0.14),transparent_35%)]" />
      <div class="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <ClockIcon class="mx-auto h-9 w-9 text-sky-blue-200" aria-hidden="true" />
        <h2 class="mt-5 text-balance text-3xl font-bold tracking-tight text-white sm:text-4xl">Votre prochaine décision peut commencer par un système plus clair.</h2>
        <p class="mx-auto mt-5 max-w-2xl text-pretty text-lg leading-8 text-sky-blue-100">Accédez à l’AppTrident, à la Méthode Trident et à la communauté pendant un an. Vous disposez de 60 jours pour vérifier que le service vous convient.</p>
        <MarketingCta page="landing" section="final" variant="light" class="mt-8" />
      </div>
    </section>
  </div>
</template>
